// app/javascript/components/Input.js
import { useRef, useEffect, useState, useLayoutEffect } from "react";
import { Box, Chip, IconButton, Tooltip, Menu, MenuItem } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import MicIcon from "@mui/icons-material/Mic";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import OpenInFullRoundedIcon from "@mui/icons-material/OpenInFullRounded";
import CloseFullscreenRoundedIcon from "@mui/icons-material/CloseFullscreenRounded";
import autosize from "autosize";
import { useNavigate } from "react-router-dom";
import { isDarkMode, themeColors } from "../utils/constants";
import { isMobileBrowser } from "../utils/user_agent";
import ImagePicker from "./ImagePicker";
import { useAppContext } from "../contexts/AppContext";
import { useChatContext } from "../contexts/ChatContext";
import { maxUpgradePathFor } from "../utils/max_upgrade_trial";
import { persistReasoningMode } from "./Chat/reasoningModes";
import {
  persistResponseLength,
  responseLengthForUser,
  responseLengthPickerEnabledForUser,
} from "./Chat/responsePreferences";
import {
  buildSlashCommands,
  filterSlashCommands,
} from "./Chat/composerCommands";
import SlashCommandMenu from "./Chat/SlashCommandMenu";
import ComposerPlusMenu from "./Chat/ComposerPlusMenu";
import ComposerAddSheet from "./Chat/ComposerAddSheet";
import EditIcon from '@mui/icons-material/Edit';
import {
  CHAT_INPUT_ACTIONS,
  deriveChatInputActionState,
  hasComposerContent,
} from "./Chat/chatInputActionModel";
import { QUEUED_MESSAGE_STATUS } from "./Chat/queuedChatMessages";
import {
  LIVE_TICKER_PREVIEW_DEBOUNCE_MS,
  tickerPreviewEnabledForEnvironment,
  tickerPreviewQueryForDraft,
  tickerPreviewRowsForEvent,
} from "./Chat/liveTickerPreviewModel";

const LIVE_TICKER_PREVIEW_ENABLED = tickerPreviewEnabledForEnvironment(
  process.env.NODE_ENV,
);

const Input = (props) => {
  const chatInputStartHeight = 28;
  // Claude-style card: textarea on top, controls row pinned beneath it.
  const controlsRowHeight = 44;
  const composerHeightTransition =
    "min-height 260ms ease, max-height 260ms ease";
  const maxAttachmentBytes = 12 * 1024 * 1024; // keep in sync with backend upload limit
  const ENABLE_VOICE_CONTROLS = false; // Temporary kill switch for wake/speak controls

  const {
    onFocus,
    onBlur,
    question,
    setQuestion,
    onQuestionSubmit,
    preventQuestionInput,
    inputPlaceholderText,
    showSignInButtons,
    userData,
    activeThread,
    editingMessage,
    onCancelEdit,
    queuedMessages = [],
    onEditQueuedMessage,
    onRemoveQueuedMessage,
    cancelRequested,
    handleAbortResponse,
    loadingResponse,
    loadingMessages,
  } = props;

  const { appProps, websocketSubscriptionRef } = useAppContext();
  const { threadViewLoading } = useChatContext();
  const { setUserData } = appProps;
  const navigate = useNavigate();

  const questionInputRef = useRef(null);
  const pickerRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const recordingChunksRef = useRef([]);
  const isRecordingRef = useRef(false);
  const speechRecognitionRef = useRef(null);
  const speechBaseRef = useRef("");
  const speechFinalRef = useRef("");
  const speechTranscriptCapturedRef = useRef(false);
  const wakeRecognitionRef = useRef(null);
  const wakeWordEnabledRef = useRef(false);
  const wakeCommandActiveRef = useRef(false);
  const wakeCommandTextRef = useRef("");
  const wakeResumeAfterManualRef = useRef(false);
  const audioChunkQueueByThreadRef = useRef({});
  const audioPlayingByThreadRef = useRef({});
  const audioElementByThreadRef = useRef({});
  const speakEnabledRef = useRef(false);
  const activeThreadUuidRef = useRef(null);
  const tickerPreviewRequestSeqRef = useRef(0);
  const latestTickerPreviewRequestIdRef = useRef(null);
  const lastTickerPreviewKeyRef = useRef(null);
  const tickerPreviewTimerRef = useRef(null);

  const [localQuestion, setLocalQuestion] = useState("");
  const [arrowVisible, setArrowVisible] = useState(false);
  const [arrowClass, setArrowClass] = useState("");
  const [isRecording, setIsRecording] = useState(false);
  const [recordingError, setRecordingError] = useState("");
  const [wakeWordEnabled, setWakeWordEnabled] = useState(() => {
    if (!ENABLE_VOICE_CONTROLS || typeof window === "undefined") return false;
    return window.localStorage.getItem("incite_wake_word_enabled") === "1";
  });
  const [wakeWordListening, setWakeWordListening] = useState(false);
  const [speakEnabled, setSpeakEnabled] = useState(() => {
    if (!ENABLE_VOICE_CONTROLS || typeof window === "undefined") return false;
    return window.localStorage.getItem("incite_speak_enabled") === "1";
  });
  const [voiceControlsAnchorEl, setVoiceControlsAnchorEl] = useState(null);
  const [editingQueuedMessageId, setEditingQueuedMessageId] = useState(null);
  const [queuedEditDraft, setQueuedEditDraft] = useState("");
  const queuedEditTextareaRef = useRef(null);

  const [pendingAttachments, setPendingAttachments] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const attachmentsRef = useRef(null);
  const [attachmentsH, setAttachmentsH] = useState(0);
  const [bubbleH, setBubbleH] = useState(chatInputStartHeight);
  const [tickerPreviewRows, setTickerPreviewRows] = useState([]);

  // Remount key for ImagePicker so native file input resets after each pick/removal
  const [pickerNonce, setPickerNonce] = useState(0);
  const [plusMenuAnchorEl, setPlusMenuAnchorEl] = useState(null);
  const [addSheetOpen, setAddSheetOpen] = useState(false);
  const [slashIndex, setSlashIndex] = useState(0);
  const [slashDismissed, setSlashDismissed] = useState(false);
  const [manuallyExpanded, setManuallyExpanded] = useState(false);
  const [isTypingNow, setIsTypingNow] = useState(false);
  const typingTimerRef = useRef(null);
  const hasInputContent = hasComposerContent({
    text: localQuestion,
    attachmentCount: pendingAttachments.length,
  });
  const {
    inputLocked,
    submitLocked,
    rightInputAction,
    rightInputDisabled,
    rightInputTooltip,
    rightInputAriaLabel,
  } = deriveChatInputActionState({
    hasInputContent,
    loadingResponse,
    cancelRequested,
    preventQuestionInput,
    loadingMessages,
    threadViewLoading,
    isRecording,
    recordingError,
    preferSendWhenContentDuringResponse: true,
    allowSubmitDuringResponse: true,
  });

  const openPhotoPicker = () => {
    if (inputLocked) return;
    pickerRef.current?.open?.("image/*");
  };

  const openFilePicker = () => {
    if (inputLocked) return;
    pickerRef.current?.open?.();
  };

  const userReasoningMode =
    userData?.user_account_info?.reasoning_mode || "standard";
  const userResponseLength = responseLengthForUser(userData);
  const showResponseLength = responseLengthPickerEnabledForUser(userData);
  const hasActiveSubscription = Boolean(userData?.has_active_subscription);
  const isMaxTierSubscriber =
    hasActiveSubscription && userData?.subscription_plan_tier === "max";

  const changeReasoningMode = async (mode) => {
    if (mode === userReasoningMode) return;
    if (mode === "max" && !isMaxTierSubscriber) {
      navigate(maxUpgradePathFor(userData));
      return;
    }
    try {
      const persisted = await persistReasoningMode(mode);
      setUserData((prev) => ({
        ...prev,
        user_account_info: {
          ...(prev.user_account_info || {}),
          reasoning_mode: persisted,
        },
      }));
    } catch (e) {
      console.error("update reasoning mode failed", e);
    }
  };

  const changeResponseLength = async (responseLength) => {
    if (responseLength === userResponseLength) return;
    const previous = userResponseLength;
    setUserData((current) => ({
      ...current,
      user_account_info: {
        ...(current?.user_account_info || {}),
        response_length: responseLength,
      },
    }));
    try {
      const persisted = await persistResponseLength(responseLength);
      setUserData((current) => ({
        ...current,
        user_account_info: {
          ...(current?.user_account_info || {}),
          response_length: persisted,
        },
      }));
    } catch (e) {
      setUserData((current) => ({
        ...current,
        user_account_info: {
          ...(current?.user_account_info || {}),
          response_length: previous,
        },
      }));
      console.error("update response length failed", e);
    }
  };

  // Slash-command palette: "/" as the first character filters the commands;
  // picking one applies it and clears the input.
  const slashCommands = buildSlashCommands({
    hasActiveSubscription,
    onReasoningMode: changeReasoningMode,
    onPickPhotos: openPhotoPicker,
    onPickFiles: openFilePicker,
  });
  const slashQuery =
    !editingMessage &&
    !inputLocked &&
    localQuestion.startsWith("/") &&
    !localQuestion.includes("\n")
      ? localQuestion.slice(1)
      : null;
  const slashMatches =
    slashQuery !== null && !slashDismissed
      ? filterSlashCommands(slashCommands, slashQuery)
      : [];
  const slashOpen = slashMatches.length > 0;
  const activeSlashIndex = slashOpen
    ? Math.min(slashIndex, slashMatches.length - 1)
    : 0;

  // Highlight starts on the first match and resets whenever the query
  // changes; an Escape dismissal lasts until the query changes again.
  useEffect(() => {
    setSlashIndex(0);
    setSlashDismissed(false);
  }, [slashQuery]);

  // Clicking or tapping outside the palette (and outside the textarea)
  // dismisses it, same as Escape.
  useEffect(() => {
    if (!slashOpen) return undefined;
    const handlePointerDown = (event) => {
      const target = event.target;
      if (target?.closest?.("[data-chat-slash-menu]")) return;
      if (target === questionInputRef.current) return;
      setSlashDismissed(true);
    };
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
    };
  }, [slashOpen]);

  // The expand toggle hides while the user is actively typing and
  // reappears after a short pause.
  const markTyping = () => {
    setIsTypingNow(true);
    if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
    typingTimerRef.current = setTimeout(() => setIsTypingNow(false), 850);
  };

  useEffect(() => {
    return () => {
      if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
    };
  }, []);

  const activeThreadUuid = activeThread?.threadUuid ?? null;

  useEffect(() => {
    if (!LIVE_TICKER_PREVIEW_ENABLED) return undefined;

    const handleTickerPreview = (event) => {
      const detail = event?.detail || {};
      const rows = tickerPreviewRowsForEvent(
        detail,
        activeThreadUuid,
        latestTickerPreviewRequestIdRef.current,
      );
      if (rows === null) return;
      setTickerPreviewRows(rows);
    };

    window.addEventListener("incite:ticker-preview", handleTickerPreview);
    return () => {
      window.removeEventListener("incite:ticker-preview", handleTickerPreview);
    };
  }, [activeThreadUuid]);

  useEffect(() => {
    if (!LIVE_TICKER_PREVIEW_ENABLED) return undefined;

    const candidateQuery = tickerPreviewQueryForDraft(localQuestion);
    const previewKey = `${activeThreadUuid || ""}:${candidateQuery}`;

    if (tickerPreviewTimerRef.current) {
      clearTimeout(tickerPreviewTimerRef.current);
      tickerPreviewTimerRef.current = null;
    }

    // Every edit gets a new deterministic candidate request. Agentic
    // resolution is owned by the submitted-message pipeline after Enter.
    tickerPreviewRequestSeqRef.current += 1;
    latestTickerPreviewRequestIdRef.current = String(
      tickerPreviewRequestSeqRef.current,
    );

    if (!activeThreadUuid || !candidateQuery) {
      lastTickerPreviewKeyRef.current = previewKey;
      setTickerPreviewRows([]);
      return undefined;
    }

    const subscription = websocketSubscriptionRef?.current;
    if (!subscription?.perform) {
      setTickerPreviewRows([]);
      return undefined;
    }

    if (previewKey !== lastTickerPreviewKeyRef.current) {
      lastTickerPreviewKeyRef.current = previewKey;
      const requestId = latestTickerPreviewRequestIdRef.current;
      tickerPreviewTimerRef.current = setTimeout(() => {
        const sent = subscription.perform("preview_tickers", {
          data: {
            query: candidateQuery,
            request_id: requestId,
          },
        });
        if (sent === false) {
          setTickerPreviewRows([]);
        }
      }, LIVE_TICKER_PREVIEW_DEBOUNCE_MS);
    }

    return () => {
      if (tickerPreviewTimerRef.current) {
        clearTimeout(tickerPreviewTimerRef.current);
        tickerPreviewTimerRef.current = null;
      }
    };
  }, [activeThreadUuid, localQuestion, websocketSubscriptionRef]);

  // Expand slides via the min/max-height transition in the style prop.
  // Collapse must be driven manually: while expanded, autosize inflates the
  // inline height (scrollHeight can never go below the expanded client
  // height), so animating min-height down just stalls against it. Measure
  // the true content height with the box zeroed, pin the current height,
  // animate `height` down to the target, then hand back to autosize.
  const hasExpandedRef = useRef(false);
  useLayoutEffect(() => {
    const el = questionInputRef.current;
    if (!el) return undefined;

    if (manuallyExpanded) {
      hasExpandedRef.current = true;
      // Heal a re-expand issued mid-collapse: the dance below leaves a
      // height-only transition behind until its timeout restores it.
      el.style.transition = composerHeightTransition;
      return undefined;
    }
    if (!hasExpandedRef.current) return undefined;

    const startHeight = el.offsetHeight;
    el.style.transition = "none";
    el.style.maxHeight = "none";
    el.style.minHeight = "0px";
    el.style.height = "0px";
    const contentHeight = el.scrollHeight;
    el.style.minHeight = "28px";
    el.style.height = `${startHeight}px`;
    void el.offsetHeight;
    el.style.transition = "height 260ms ease";
    el.style.height = `${Math.min(contentHeight, 200)}px`;

    const timer = setTimeout(() => {
      el.style.transition = composerHeightTransition;
      el.style.maxHeight = "200px";
      try { autosize.update(el); } catch {}
    }, 300);
    return () => clearTimeout(timer);
  }, [manuallyExpanded]);

  const applySlashCommand = (command) => {
    setLocalQuestion("");
    setQuestion && setQuestion("");
    setManuallyExpanded(false);
    command.run();
    requestAnimationFrame(() => {
      const el = questionInputRef.current;
      if (el) {
        try { autosize.update(el); } catch {}
      }
      recalcBubble();
    });
  };

  // This all handles focusing on the element when you change threads
  const focusInput = () => {
    const el = questionInputRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      try { autosize.update(el); } catch {}
      el.focus({ preventScroll: true });
      const len = el.value?.length ?? 0;
      try { el.setSelectionRange(len, len); } catch {}
    });
  };

  useEffect(() => {
    if (showSignInButtons || inputLocked) return;
    focusInput();
  }, [activeThread?.threadUuid, showSignInButtons, inputLocked])

  useEffect(() => {
    if (!editingMessage || showSignInButtons || inputLocked) return;
    focusInput();
  }, [editingMessage, showSignInButtons, inputLocked]);

  useEffect(() => {
    return () => {
      if (speechRecognitionRef.current) {
        try { speechRecognitionRef.current.stop(); } catch {}
        speechRecognitionRef.current = null;
      }
      if (wakeRecognitionRef.current) {
        try { wakeRecognitionRef.current.stop(); } catch {}
        wakeRecognitionRef.current = null;
      }
      if (typeof window !== "undefined" && window.speechSynthesis) {
        try { window.speechSynthesis.cancel(); } catch {}
      }
      Object.values(audioElementByThreadRef.current || {}).forEach((audio) => {
        try {
          audio.pause();
          audio.src = "";
        } catch {}
      });
      audioElementByThreadRef.current = {};
      audioPlayingByThreadRef.current = {};
      audioChunkQueueByThreadRef.current = {};
      const recorder = mediaRecorderRef.current;
      if (recorder && recorder.state !== "inactive") {
        try { recorder.stop(); } catch {}
      }
      const stream = mediaStreamRef.current;
      if (stream) {
        stream.getTracks().forEach((t) => t.stop());
        mediaStreamRef.current = null;
      }
    };
  }, []);

  // --- Helpers for dedupe and stable URL management ------------------------
  const fileKey = (it) => {
    if (!it) return "__nil__";
    if (typeof it === "string") return it;
    if (it.url) return it.url;
    const f = it.file || it.blob || it;
    return [f.name, f.size, f.type, f.lastModified].join("|");
  };

  // Stable map of fileKey -> objectURL (only for blobs we create)
  const urlMapRef = useRef(new Map());


  const computePreviewUrls = (list) => {
    const map = urlMapRef.current;
    const urls = [];
    const presentKeys = new Set();

    for (const it of list) {
      if (!it) continue;

      if (typeof it === "string") {
        urls.push(it);
        continue;
      }
      if (it.url) {
        urls.push(it.url);
        continue;
      }

      const f = it.file || it.blob || it;
      const k = [f.name, f.size, f.type, f.lastModified].join("|");
      presentKeys.add(k);

      let u = map.get(k);
      if (!u) {
        try {
          u = URL.createObjectURL(f);
          map.set(k, u);
        } catch {
          u = null;
        }
      }
      if (u) urls.push(u);
    }

    // Revoke URLs for items no longer present
    for (const [k, u] of Array.from(map.entries())) {
      if (!presentKeys.has(k)) {
        try { URL.revokeObjectURL(u); } catch {}
        map.delete(k);
      }
    }

    return urls;
  };

  const normalizeMime = (mime = "") => mime.split(";")[0].trim().toLowerCase();

  const pickAudioMimeType = () => {
    if (typeof MediaRecorder === "undefined") return "";
    const candidates = [
      "audio/webm;codecs=opus",
      "audio/webm",
      "audio/ogg;codecs=opus",
      "audio/ogg",
      "audio/mp4",
      "audio/mpeg",
      "audio/wav",
    ];
    return candidates.find((t) => MediaRecorder.isTypeSupported(t)) || "";
  };

  const extensionFromMime = (mime = "") => {
    const clean = normalizeMime(mime);
    if (clean === "audio/webm") return "webm";
    if (clean === "audio/ogg") return "ogg";
    if (clean === "audio/mpeg") return "mp3";
    if (clean === "audio/mp4") return "m4a";
    if (clean === "audio/wav" || clean === "audio/x-wav") return "wav";
    const slashIdx = clean.indexOf("/");
    return slashIdx > -1 ? clean.slice(slashIdx + 1) : "webm";
  };

  const wakePhraseRegex = /\bhey\s+(?:incite|in\s*cite|insight)\b/i;
  const wakeSendRegex = /\b(?:and go|send|done)\b[\s.!?]*$/i;
  const WAKE_WORD_DEBUG = false;

  const wakeLog = (...args) => {
    if (!WAKE_WORD_DEBUG) return;
    console.log("[WakeWord]", ...args);
  };

  const getSpeechRecognitionCtor = () => {
    if (typeof window === "undefined") return null;
    return window.SpeechRecognition || window.webkitSpeechRecognition || null;
  };

  const speakSupported =
    typeof window !== "undefined" &&
    typeof window.Audio !== "undefined";
  const BASE_AUDIO_PLAYBACK_RATE = 1.12;
  const MAX_AUDIO_PLAYBACK_RATE = 1.35;
  const BACKLOG_PLAYBACK_STEP = 0.05;

  const stopSpeechPlayback = () => {
    const byThread = audioElementByThreadRef.current || {};
    Object.entries(byThread).forEach(([threadKey, audio]) => {
      try {
        audio.pause();
        audio.src = "";
      } catch {}
      delete byThread[threadKey];
    });
    audioElementByThreadRef.current = byThread;
    audioPlayingByThreadRef.current = {};
    audioChunkQueueByThreadRef.current = {};
  };

  const base64ToUint8Array = (base64) => {
    const binary = window.atob(base64 || "");
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  };

  const audioMimeFromFormat = (format) => {
    const normalized = String(format || "").toLowerCase();
    if (normalized === "wav") return "audio/wav";
    if (normalized === "ogg") return "audio/ogg";
    return "audio/mpeg";
  };

  const pumpAudioChunkQueue = (threadKey) => {
    if (!speakSupported || !threadKey || !speakEnabledRef.current) return;
    if (audioPlayingByThreadRef.current[threadKey]) return;
    const queue = audioChunkQueueByThreadRef.current[threadKey] || [];
    if (!queue.length) return;
    const backlogDepth = queue.length;

    const nextChunk = queue.shift();
    audioChunkQueueByThreadRef.current = {
      ...(audioChunkQueueByThreadRef.current || {}),
      [threadKey]: queue,
    };

    let audioUrl = "";
    try {
      const bytes = base64ToUint8Array(nextChunk.audioChunkB64);
      const blob = new Blob([bytes], { type: audioMimeFromFormat(nextChunk.audioFormat) });
      audioUrl = URL.createObjectURL(blob);
      const audio = new Audio(audioUrl);
      const playbackRate = Math.min(
        MAX_AUDIO_PLAYBACK_RATE,
        BASE_AUDIO_PLAYBACK_RATE + Math.max(0, backlogDepth - 1) * BACKLOG_PLAYBACK_STEP,
      );
      audio.playbackRate = playbackRate;
      if ("preservesPitch" in audio) {
        audio.preservesPitch = false;
      } else if ("mozPreservesPitch" in audio) {
        audio.mozPreservesPitch = false;
      } else if ("webkitPreservesPitch" in audio) {
        audio.webkitPreservesPitch = false;
      }
      audioPlayingByThreadRef.current = {
        ...(audioPlayingByThreadRef.current || {}),
        [threadKey]: true,
      };
      audioElementByThreadRef.current = {
        ...(audioElementByThreadRef.current || {}),
        [threadKey]: audio,
      };

      const finish = () => {
        try { URL.revokeObjectURL(audioUrl); } catch {}
        audioPlayingByThreadRef.current = {
          ...(audioPlayingByThreadRef.current || {}),
          [threadKey]: false,
        };
        audioElementByThreadRef.current = {
          ...(audioElementByThreadRef.current || {}),
          [threadKey]: null,
        };
        if (speakEnabledRef.current) {
          pumpAudioChunkQueue(threadKey);
        }
      };

      audio.onended = finish;
      audio.onerror = finish;

      audio.play().catch(() => {
        finish();
      });
    } catch {
      if (audioUrl) {
        try { URL.revokeObjectURL(audioUrl); } catch {}
      }
      audioPlayingByThreadRef.current = {
        ...(audioPlayingByThreadRef.current || {}),
        [threadKey]: false,
      };
      if (speakEnabledRef.current) {
        pumpAudioChunkQueue(threadKey);
      }
    }
  };

  const enqueueAudioChunk = ({ threadUuid, audioChunkB64, audioFormat, audioSeq }) => {
    if (!speakSupported || !audioChunkB64) return;
    if (!speakEnabledRef.current) return;
    const activeThreadUuid = activeThreadUuidRef.current;
    const threadKey = threadUuid || activeThreadUuid || "global";

    const existing = audioChunkQueueByThreadRef.current[threadKey] || [];
    const nextQueue = existing.concat({
      audioChunkB64,
      audioFormat: audioFormat || "mp3",
      audioSeq: Number.isFinite(Number(audioSeq)) ? Number(audioSeq) : null,
    });

    nextQueue.sort((a, b) => {
      if (a.audioSeq == null || b.audioSeq == null) return 0;
      return a.audioSeq - b.audioSeq;
    });

    audioChunkQueueByThreadRef.current = {
      ...(audioChunkQueueByThreadRef.current || {}),
      [threadKey]: nextQueue,
    };

    pumpAudioChunkQueue(threadKey);
  };

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handler = (event) => {
      const detail = event?.detail || {};
      enqueueAudioChunk({
        threadUuid: detail.threadUuid,
        audioChunkB64: detail.audioChunkB64,
        audioFormat: detail.audioFormat,
        audioSeq: detail.audioSeq,
      });
    };
    window.addEventListener("incite:audio-chunk", handler);
    return () => {
      window.removeEventListener("incite:audio-chunk", handler);
    };
  }, []);

  useEffect(() => {
    speakEnabledRef.current = Boolean(speakEnabled);
    activeThreadUuidRef.current = activeThread?.threadUuid || null;
  }, [speakEnabled, activeThread?.threadUuid]);

  useEffect(() => {
    activeThreadUuidRef.current = activeThread?.threadUuid || null;
    if (!speakEnabledRef.current) {
      return;
    }
    stopSpeechPlayback();
  }, [activeThread?.threadUuid]);

  const stopWakeWordListening = ({ keepEnabled = false } = {}) => {
    wakeLog("stopWakeWordListening", { keepEnabled });
    wakeWordEnabledRef.current = keepEnabled;
    wakeCommandActiveRef.current = false;
    wakeCommandTextRef.current = "";

    const rec = wakeRecognitionRef.current;
    wakeRecognitionRef.current = null;
    setWakeWordListening(false);

    if (rec) {
      rec.onresult = null;
      rec.onerror = null;
      rec.onend = null;
      try { rec.stop(); } catch {}
    }
  };

  const submitWakePhrase = (rawText) => {
    const finalText = String(rawText || "").replace(wakeSendRegex, "").replace(/\s+/g, " ").trim();
    wakeLog("submitWakePhrase called", { rawText, finalText });
    if (!finalText) {
      wakeCommandActiveRef.current = false;
      wakeCommandTextRef.current = "";
      return;
    }

    if (submitLocked || isRecordingRef.current) {
      wakeLog("submitWakePhrase blocked", {
        preventQuestionInput: submitLocked,
        isRecording: isRecordingRef.current,
      });
      wakeCommandActiveRef.current = true;
      wakeCommandTextRef.current = finalText;
      setLocalQuestion(finalText);
      return;
    }

    wakeLog("submitWakePhrase submitting", { finalText });
    onQuestionSubmit(finalText, null, null, [], {
      responseLength: userResponseLength,
    });
    wakeCommandActiveRef.current = false;
    wakeCommandTextRef.current = "";
    setLocalQuestion("");
    setQuestion && setQuestion("");

    requestAnimationFrame(() => {
      measureAttachments();
      recalcBubble();
    });
  };

  const applyWakeChunk = (rawChunk) => {
    const chunk = String(rawChunk || "").replace(/\s+/g, " ").trim();
    wakeLog("applyWakeChunk", { rawChunk, chunk });
    if (!chunk) return;

    let textToAppend = chunk;
    if (!wakeCommandActiveRef.current) {
      const match = chunk.match(wakePhraseRegex);
      if (!match) {
        wakeLog("wake phrase not found in chunk");
        return;
      }
      wakeLog("wake phrase detected", { match: match[0] });
      wakeCommandActiveRef.current = true;
      textToAppend = chunk.slice((match.index || 0) + match[0].length).trim();
      wakeCommandTextRef.current = "";
    }

    if (textToAppend) {
      wakeCommandTextRef.current = [wakeCommandTextRef.current, textToAppend]
        .filter(Boolean)
        .join(" ")
        .replace(/\s+/g, " ")
        .trim();
    }

    if (wakeCommandTextRef.current) {
      wakeLog("wake command text updated", { wakeCommandText: wakeCommandTextRef.current });
      setLocalQuestion(wakeCommandTextRef.current);
      requestAnimationFrame(() => {
        measureAttachments();
      });
    }

    if (wakeSendRegex.test(wakeCommandTextRef.current)) {
      wakeLog("send phrase detected", { wakeCommandText: wakeCommandTextRef.current });
      submitWakePhrase(wakeCommandTextRef.current);
    }
  };

  const startWakeWordListening = () => {
    wakeLog("startWakeWordListening attempt", {
      hasRecognition: !!wakeRecognitionRef.current,
      enabled: wakeWordEnabledRef.current,
      showSignInButtons,
      isRecording: isRecordingRef.current,
    });
    if (wakeRecognitionRef.current || !wakeWordEnabledRef.current || showSignInButtons || isRecordingRef.current) {
      return;
    }

    const SpeechRecognition = getSpeechRecognitionCtor();
    if (!SpeechRecognition) {
      wakeLog("startWakeWordListening unsupported");
      setRecordingError("Wake word is not supported in this browser.");
      setWakeWordEnabled(false);
      wakeWordEnabledRef.current = false;
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = "en-US";

    recognition.onresult = (event) => {
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const res = event.results[i];
        const transcript = res[0]?.transcript || "";
        wakeLog("speech result", { transcript, isFinal: !!res?.isFinal });
        if (!res?.isFinal) continue;
        applyWakeChunk(transcript);
      }
    };

    recognition.onerror = (event) => {
      wakeLog("recognition error", event?.error || event);
      if (event?.error === "not-allowed" || event?.error === "service-not-allowed") {
        setRecordingError("Microphone access is required for wake word mode.");
        setWakeWordEnabled(false);
        wakeWordEnabledRef.current = false;
      }
    };

    recognition.onend = () => {
      wakeLog("recognition ended; will attempt restart if enabled");
      wakeRecognitionRef.current = null;
      setWakeWordListening(false);
      if (!wakeWordEnabledRef.current || showSignInButtons || isRecordingRef.current) return;
      setTimeout(() => {
        if (!wakeWordEnabledRef.current || wakeRecognitionRef.current || showSignInButtons || isRecordingRef.current) {
          return;
        }
        startWakeWordListening();
      }, 250);
    };

    wakeRecognitionRef.current = recognition;
    try {
      recognition.start();
      wakeLog("recognition started");
      setWakeWordListening(true);
    } catch (err) {
      wakeLog("recognition start failed", err);
      wakeRecognitionRef.current = null;
      setWakeWordListening(false);
    }
  };

  // Append-only handler (delta-safe) and then remount picker to reset native input
  const currentKeysRef = useRef(new Set());
  useEffect(() => {
    currentKeysRef.current = new Set(pendingAttachments.map(fileKey));
  }, [pendingAttachments]);

  const handleFiles = (files) => {
    const cur = currentKeysRef.current;
    const incoming = Array.isArray(files) ? files : (files ? [files] : []);
    const delta = incoming.filter((f) => !cur.has(fileKey(f)));

    if (delta.length) {
      setPendingAttachments((prev) => [...prev, ...delta]);
      requestAnimationFrame(measureAttachments);
    }

    // Force-reset the underlying <input type="file"> so selecting the same file again fires `change`
    setPickerNonce((n) => n + 1);
  };

  const stopRecording = ({ fromSpeech = false } = {}) => {
    if (!isRecordingRef.current) return;
    isRecordingRef.current = false;
    setIsRecording(false);

    if (!fromSpeech && speechRecognitionRef.current) {
      try {
        speechRecognitionRef.current.stop();
      } catch {}
    }
    speechRecognitionRef.current = null;

    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      try {
        recorder.stop();
      } catch {}
    }
  };

  const startRecording = async () => {
    if (inputLocked) return;
    setRecordingError("");
    if (wakeWordEnabledRef.current && wakeRecognitionRef.current) {
      wakeResumeAfterManualRef.current = true;
      stopWakeWordListening({ keepEnabled: true });
    } else {
      wakeResumeAfterManualRef.current = false;
    }
    if (!navigator?.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      setRecordingError("Voice recording is not supported in this browser.");
      if (wakeResumeAfterManualRef.current) {
        wakeResumeAfterManualRef.current = false;
        if (wakeWordEnabledRef.current) {
          startWakeWordListening();
        }
      }
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = pickAudioMimeType();
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      speechTranscriptCapturedRef.current = false;

      mediaRecorderRef.current = recorder;
      mediaStreamRef.current = stream;
      recordingChunksRef.current = [];

      recorder.ondataavailable = (evt) => {
        if (evt.data && evt.data.size > 0) {
          recordingChunksRef.current.push(evt.data);
        }
      };

      recorder.onerror = () => {
        setRecordingError("Audio recording failed.");
        stopRecording();
      };

      recorder.onstop = () => {
        try {
          const chunks = recordingChunksRef.current || [];
          recordingChunksRef.current = [];

          const streamRef = mediaStreamRef.current;
          if (streamRef) {
            streamRef.getTracks().forEach((t) => t.stop());
            mediaStreamRef.current = null;
          }

          mediaRecorderRef.current = null;

          // If live speech recognition already captured the utterance into the input,
          // skip attaching the recorded audio file to avoid duplicate payloads.
          if (speechTranscriptCapturedRef.current) {
            speechTranscriptCapturedRef.current = false;
            return;
          }

          if (!chunks.length) return;

          const blobType = recorder.mimeType || mimeType || "audio/webm";
          const blob = new Blob(chunks, { type: blobType });
          if (blob.size > maxAttachmentBytes) {
            setRecordingError("Audio clip is too large. Please record a shorter message.");
            return;
          }

          const ext = extensionFromMime(blobType);
          const stamp = new Date().toISOString().replace(/[:.]/g, "-");
          const name = `voice-${stamp}.${ext}`;
          const file = new File([blob], name, { type: blobType });

          setPendingAttachments((prev) => [...prev, { file, mime: blobType }]);
          requestAnimationFrame(measureAttachments);
        } finally {
          if (wakeResumeAfterManualRef.current) {
            wakeResumeAfterManualRef.current = false;
            if (wakeWordEnabledRef.current) {
              startWakeWordListening();
            }
          }
        }
      };

      const SpeechRecognition =
        typeof window !== "undefined" &&
        (window.SpeechRecognition || window.webkitSpeechRecognition);

      if (SpeechRecognition) {
        const recognition = new SpeechRecognition();
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang = "en-US";

        speechBaseRef.current = localQuestion.trim();
        speechFinalRef.current = "";

        recognition.onresult = (event) => {
          let interim = "";
          for (let i = event.resultIndex; i < event.results.length; i += 1) {
            const res = event.results[i];
            const transcript = res[0]?.transcript || "";
            if (transcript.trim()) {
              speechTranscriptCapturedRef.current = true;
            }
            if (res.isFinal) {
              speechFinalRef.current += transcript;
            } else {
              interim += transcript;
            }
          }

          const combined = [speechBaseRef.current, speechFinalRef.current, interim]
            .filter(Boolean)
            .join(" ")
            .replace(/\s+/g, " ")
            .trim();

          if (combined) {
            setLocalQuestion(combined);
            requestAnimationFrame(() => {
              measureAttachments();
            });
          }
        };

        recognition.onerror = () => {
          setRecordingError("Speech recognition encountered an error.");
        };

        recognition.onend = () => {
          if (isRecordingRef.current) {
            stopRecording({ fromSpeech: true });
          }
        };

        speechRecognitionRef.current = recognition;
        try {
          recognition.start();
        } catch {}
      }

      recorder.start();
      isRecordingRef.current = true;
      setIsRecording(true);
    } catch (err) {
      setRecordingError("Microphone access was denied.");
      if (wakeResumeAfterManualRef.current) {
        wakeResumeAfterManualRef.current = false;
        if (wakeWordEnabledRef.current) {
          startWakeWordListening();
        }
      }
    }
  };

  const toggleRecording = () => {
    if (isRecordingRef.current) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  const wakeWordSupported = Boolean(getSpeechRecognitionCtor());

  const toggleWakeWordMode = () => {
    if (!ENABLE_VOICE_CONTROLS) return;
    if (!wakeWordSupported) {
      setRecordingError("Wake word is not supported in this browser.");
      return;
    }
    setWakeWordEnabled((prev) => !prev);
  };

  const toggleSpeakMode = () => {
    if (!ENABLE_VOICE_CONTROLS) return;
    if (!speakSupported) {
      setRecordingError("Voice playback is not supported in this browser.");
      return;
    }
    setSpeakEnabled((prev) => !prev);
  };

  const openVoiceControls = (event) => {
    if (!ENABLE_VOICE_CONTROLS) return;
    setVoiceControlsAnchorEl(event.currentTarget);
  };

  const closeVoiceControls = () => {
    setVoiceControlsAnchorEl(null);
  };

  useEffect(() => {
    if (!ENABLE_VOICE_CONTROLS) {
      wakeWordEnabledRef.current = false;
      setWakeWordEnabled(false);
      setWakeWordListening(false);
      stopWakeWordListening();
      if (typeof window !== "undefined") {
        window.localStorage.setItem("incite_wake_word_enabled", "0");
      }
      return;
    }
    wakeWordEnabledRef.current = wakeWordEnabled;
    if (typeof window !== "undefined") {
      window.localStorage.setItem("incite_wake_word_enabled", wakeWordEnabled ? "1" : "0");
    }
  }, [wakeWordEnabled, ENABLE_VOICE_CONTROLS]);

  useEffect(() => {
    if (!wakeWordEnabled) {
      stopWakeWordListening();
      return;
    }
    if (showSignInButtons || isRecording) {
      stopWakeWordListening({ keepEnabled: true });
      return;
    }
    startWakeWordListening();

    return () => {
      if (!wakeWordEnabledRef.current) return;
      stopWakeWordListening({ keepEnabled: true });
    };
  }, [wakeWordEnabled, showSignInButtons, isRecording]);

  useEffect(() => {
    if (!ENABLE_VOICE_CONTROLS) {
      if (typeof window !== "undefined") {
        window.localStorage.setItem("incite_speak_enabled", "0");
      }
      setSpeakEnabled(false);
      stopSpeechPlayback();
      return;
    }
    if (typeof window !== "undefined") {
      window.localStorage.setItem("incite_speak_enabled", speakEnabled ? "1" : "0");
    }
    if (!speakEnabled) {
      stopSpeechPlayback();
    }
  }, [speakEnabled, ENABLE_VOICE_CONTROLS]);
  
  useEffect(() => {
    const hasContent = localQuestion.trim().length > 0 || pendingAttachments.length > 0;
    if (hasContent && !arrowVisible) {
      setArrowVisible(true);
      setArrowClass("arrow-animate-in");
    } else if (!hasContent && arrowVisible) {
      setArrowClass("arrow-animate-out");
      const t = setTimeout(() => setArrowVisible(false), 20);
      return () => clearTimeout(t);
    }
  }, [localQuestion, arrowVisible, pendingAttachments.length]);

  useEffect(() => {
    if (showSignInButtons) return undefined;
    const el = questionInputRef.current;
    if (!el) return undefined;
    autosize(el);
    return () => {
      autosize.destroy(el);
    };
  }, [showSignInButtons]);

  useEffect(() => {
    setLocalQuestion(question || "");
  }, [question]);

  useLayoutEffect(() => {
    const el = questionInputRef.current;
    if (!el) return;

    requestAnimationFrame(() => {
      autosize.update(el);
      recalcBubble();
    });
  }, [localQuestion, editingMessage, attachmentsH]);

  // Build stable preview URLs (only revoke for removed items)
  useEffect(() => {
    setPreviewUrls(computePreviewUrls(pendingAttachments));
  }, [pendingAttachments]);

  const measureAttachments = () => {
    setAttachmentsH(attachmentsRef.current ? attachmentsRef.current.offsetHeight : 0);
  };

  const editTextareaOuterGap = editingMessage ? 8 : 0;
  const queuedComposerMessages = Array.isArray(queuedMessages)
    ? queuedMessages.filter(
        (item) => item?.queueStatus === QUEUED_MESSAGE_STATUS.QUEUED,
      )
    : [];
  const activeQueuedEditItem = queuedComposerMessages.find(
    (item) => item?.clientQueueId === editingQueuedMessageId,
  );

  useEffect(() => {
    if (!editingQueuedMessageId) return;
    if (activeQueuedEditItem) return;
    setEditingQueuedMessageId(null);
    setQueuedEditDraft("");
  }, [activeQueuedEditItem, editingQueuedMessageId]);

  useEffect(() => {
    if (!editingQueuedMessageId) return;
    requestAnimationFrame(() => {
      const el = queuedEditTextareaRef.current;
      if (!el) return;
      el.focus({ preventScroll: true });
      const len = el.value?.length ?? 0;
      try { el.setSelectionRange(len, len); } catch {}
    });
  }, [editingQueuedMessageId]);

  const startQueuedMessageEdit = (queuedItem) => {
    if (!queuedItem?.clientQueueId) return;
    setEditingQueuedMessageId(queuedItem.clientQueueId);
    setQueuedEditDraft(queuedItem.message || "");
  };

  const cancelQueuedMessageEdit = () => {
    setEditingQueuedMessageId(null);
    setQueuedEditDraft("");
  };

  const saveQueuedMessageEdit = () => {
    const cleaned = (queuedEditDraft || "").trim();
    if (!editingQueuedMessageId || !cleaned) return;
    const saved = onEditQueuedMessage?.(
      activeThread?.threadUuid,
      editingQueuedMessageId,
      cleaned,
    );
    if (saved === false) return;
    cancelQueuedMessageEdit();
  };

  const recalcBubble = () => {
    const el = questionInputRef.current;
    const h = el ? el.offsetHeight : chatInputStartHeight;
    setBubbleH(Math.max(chatInputStartHeight, h + editTextareaOuterGap));
  };

  const handleCancelEdit = () => {
    if (isRecordingRef.current) {
      stopRecording();
    }
    setLocalQuestion("");
    setPendingAttachments([]);
    setAttachmentsH(0);
    setManuallyExpanded(false);
    setQuestion && setQuestion("");
    onCancelEdit?.();

    requestAnimationFrame(() => {
      measureAttachments();
      recalcBubble();
    });
  };

  useLayoutEffect(() => {
    const el = questionInputRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => recalcBubble());
    ro.observe(el);
    return () => ro.disconnect();
  }, [editTextareaOuterGap]);

  // Bubble height follows textarea height (still keep existing trigger points)
  useEffect(() => {
    requestAnimationFrame(recalcBubble);
  }, [attachmentsH, localQuestion]);

  const submitQuestion = () => {
    if (isRecordingRef.current) {
      return;
    }
    if ((localQuestion.trim().length > 0 || pendingAttachments.length > 0) && !submitLocked) {
      const submitted = onQuestionSubmit(
        localQuestion,
        null,
        null,
        pendingAttachments,
        { responseLength: userResponseLength },
      );
      if (submitted === false) {
        return;
      }

      // Clear UI state
      setLocalQuestion("");
      setPendingAttachments([]);
      setAttachmentsH(0);
      setManuallyExpanded(false);
      setQuestion && setQuestion("");

      // Revoke all blob URLs and reset map
      for (const [, u] of urlMapRef.current.entries()) {
        try { URL.revokeObjectURL(u); } catch {}
      }
      urlMapRef.current.clear();
      setPreviewUrls([]);

      // Reset textarea and picker; let autosize recompute, then sync bubble
      setPickerNonce((n) => n + 1);
      requestAnimationFrame(() => {
        const el = questionInputRef.current;
        if (el) {
          try { autosize.update(el); } catch {}
          measureAttachments();
          recalcBubble();
        } else {
          setBubbleH(chatInputStartHeight);
        }
      });
    }
  };

  const classTextAreaContainer = "absolute-textarea-container image-uploader"
  const isMobileInput = isMobileBrowser();
  const editTextareaTopOffset = editingMessage ? 28 : 0;
  const tickerPreviewTrayHeight = tickerPreviewRows.length > 0 ? 40 : 0;
  const handleRightInputAction = () => {
    if (rightInputAction === CHAT_INPUT_ACTIONS.STOP) {
      handleAbortResponse?.();
      return;
    }
    if (rightInputAction === CHAT_INPUT_ACTIONS.SEND) {
      if (isRecordingRef.current) {
        stopRecording();
      }
      requestAnimationFrame(submitQuestion);
      return;
    }
    toggleRecording();
  };

  // Filled action circle (Claude-style): inverse fill for mic/send, tinted
  // fills for the recording and stop states.
  const isRecordingMicAction = rightInputAction === "mic" && isRecording;
  const actionCircleBg =
    rightInputAction === "stop"
      ? "rgba(107,114,128,0.22)"
      : isRecordingMicAction
      ? "rgba(248,113,113,0.14)"
      : isDarkMode
      ? "#f5f5f5"
      : "#121212";
  const actionIconColor = isRecordingMicAction
    ? "#F87171"
    : isDarkMode
    ? "#121212"
    : "#f5f5f5";

  // The expand toggle is always available while the composer is idle; it
  // fades out during active typing and back in after a short pause.
  const expandToggleVisible = !editingMessage && !inputLocked;

  const onTextareaPaste = async (e) => {
    const fl = e.clipboardData?.files || [];
    if (fl.length) {
      const hasImage = Array.from(fl).some((f) => (f.type || "").startsWith("image/"));
      if (hasImage) {
        e.preventDefault();
        await pickerRef.current?.ingest(fl);
        requestAnimationFrame(measureAttachments);
      }
    }
  };

  const removeAttachmentAt = (idx) => {
    setPendingAttachments((prev) => {
      const next = prev.filter((_, i) => i !== idx);
      return next;
    });

    // Reset native input so user can re-add the same file immediately
    setPickerNonce((n) => n + 1);

    requestAnimationFrame(measureAttachments);
  };


  return (
    <Box style={{ width: "100%", display: "flex", justifyContent: "center", backgroundColor: themeColors.chatBoxColor }}>
      <Box
        className="question-input-style"
      >
        <Box
          className="input-container"
          sx={{
            backgroundColor: themeColors.chatBoxColor,
            transition: "left 250ms, right 250ms",
            ...(props.isDesktop && {
              left: `${props.chatLayout?.contentLeft ?? Math.max(props.sidebarOffset, 0)}px`,
              right: `${props.chatLayout?.contentRight ?? Math.max(-props.sidebarOffset, 0)}px`,
              width: "auto",
            }),
          }}
        >
          <Box className="inner-input-container" sx={{ backgroundColor: themeColors.chatBoxColor }}>
            <Box className={classTextAreaContainer}>
              {!showSignInButtons && (
                <Box sx={{ display: "flex" }}>

                  {ENABLE_VOICE_CONTROLS && (
                    <>
                      <Tooltip
                        title="Voice controls"
                        arrow
                        enterDelay={250}
                        describeChild
                      >
                        <IconButton
                          onClick={openVoiceControls}
                          disabled={inputLocked}
                          sx={{
                            position: "absolute",
                            right: isMobileInput ? "-33px" : "-37px",
                            bottom: "50px",
                            width: 112,
                            height: 24,
                            zIndex: 5,
                            borderRadius: "999px",
                            backgroundColor: (wakeWordEnabled || speakEnabled)
                              ? (isDarkMode ? "rgba(34,197,94,0.30)" : "rgba(34,197,94,0.18)")
                              : (isDarkMode ? "rgba(255,255,255,0.1)" : "#ECEEF3"),
                            color: (wakeWordEnabled || speakEnabled) ? "#22c55e" : (isDarkMode ? "#fff" : "#000"),
                            transition: "all 0.2s ease",
                            "&:hover": {
                              backgroundColor: (wakeWordEnabled || speakEnabled)
                                ? (isDarkMode ? "rgba(34,197,94,0.42)" : "rgba(34,197,94,0.28)")
                                : (isDarkMode ? "rgba(255,255,255,0.25)" : "#ECEEF3"),
                            },
                            "&:active": {
                              transform: "scale(0.97)",
                            },
                          }}
                          aria-label="Voice controls"
                        >
                          <Box sx={{ fontSize: 9.5, fontWeight: 700, letterSpacing: "0.01em" }}>
                            Voice controls
                          </Box>
                        </IconButton>
                      </Tooltip>

                      <Menu
                        anchorEl={voiceControlsAnchorEl}
                        open={Boolean(voiceControlsAnchorEl)}
                        onClose={closeVoiceControls}
                        anchorOrigin={{ vertical: "top", horizontal: "right" }}
                        transformOrigin={{ vertical: "bottom", horizontal: "right" }}
                      >
                        <MenuItem
                          onClick={() => {
                            toggleWakeWordMode();
                            closeVoiceControls();
                          }}
                          disabled={!wakeWordSupported}
                          sx={{
                            minWidth: 140,
                            color: wakeWordEnabled ? "#22c55e" : "inherit",
                            fontWeight: wakeWordEnabled ? 700 : 500,
                          }}
                        >
                          {wakeWordEnabled && wakeWordListening ? "Listen (active)" : "Listen"}
                        </MenuItem>
                        <MenuItem
                          onClick={() => {
                            toggleSpeakMode();
                            closeVoiceControls();
                          }}
                          disabled={!speakSupported}
                          sx={{
                            minWidth: 140,
                            color: speakEnabled ? "#22c55e" : "inherit",
                            fontWeight: speakEnabled ? 700 : 500,
                          }}
                        >
                          Speak
                        </MenuItem>
                      </Menu>
                    </>
                  )}

                  {tickerPreviewRows.length > 0 && (
                    <Box
                      data-live-ticker-preview
                      role="status"
                      aria-live="polite"
                      aria-label={`Detected tickers: ${tickerPreviewRows
                        .map((row) => row.ticker)
                        .join(", ")}`}
                      sx={{
                        position: "absolute",
                        left: "8px",
                        right: "auto",
                        bottom: `calc(${bubbleH + controlsRowHeight}px + 4px)`,
                        height: "32px",
                        width: "fit-content",
                        maxWidth: "calc(100% - 16px)",
                        boxSizing: "border-box",
                        display: "flex",
                        alignItems: "center",
                        gap: "7px",
                        px: "8px",
                        borderRadius: "999px",
                        backgroundColor: isDarkMode ? "#252525" : "#FFFFFF",
                        border: isDarkMode
                          ? "1px solid rgba(255,255,255,0.10)"
                          : "1px solid rgba(15,23,42,0.10)",
                        boxShadow: isDarkMode
                          ? "0 4px 14px rgba(0,0,0,0.28)"
                          : "0 4px 14px rgba(15,23,42,0.10)",
                        overflowX: "auto",
                        overflowY: "hidden",
                        zIndex: 6,
                        pointerEvents: "auto",
                        touchAction: "pan-x",
                        scrollbarWidth: "none",
                        "&::-webkit-scrollbar": { display: "none" },
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          flex: "0 0 auto",
                          fontSize: "11px",
                          fontWeight: 600,
                          color: isDarkMode
                            ? "rgba(255,255,255,0.58)"
                            : "rgba(18,18,18,0.52)",
                        }}
                      >
                        Candidates
                      </Box>
                      {tickerPreviewRows.map((row) => (
                        <Chip
                          key={row.id}
                          label={row.ticker}
                          title={row.name || row.ticker}
                          size="small"
                          sx={{
                            flex: "0 0 auto",
                            height: "26px",
                            borderRadius: "999px",
                            fontSize: "12px",
                            fontWeight: 700,
                            color: isDarkMode ? "#E8F0FF" : "#1E3A5F",
                            backgroundColor: isDarkMode
                              ? "rgba(96,165,250,0.16)"
                              : "rgba(59,130,246,0.10)",
                            border: isDarkMode
                              ? "1px solid rgba(147,197,253,0.22)"
                              : "1px solid rgba(37,99,235,0.16)",
                          }}
                        />
                      ))}
                    </Box>
                  )}

                  {queuedComposerMessages.length > 0 && (
                    <Box
                      data-chat-queued-composer-tray
                      sx={{
                        position: "absolute",
                        left: 0,
                        right: 0,
                        bottom: `calc(${bubbleH + controlsRowHeight + tickerPreviewTrayHeight}px + 8px)`,
                        display: "flex",
                        flexDirection: "column",
                        gap: "6px",
                        zIndex: 6,
                        pointerEvents: "none",
                      }}
                    >
                      {queuedComposerMessages.map((queuedItem, idx) => {
                        const queuedItemId = queuedItem.clientQueueId || queuedItem.id || idx;
                        const isEditingQueuedItem =
                          editingQueuedMessageId === queuedItem.clientQueueId;
                        const queuedLabel = idx === 0 ? "Queued next" : `Queued ${idx + 1}`;

                        return (
                          <Box
                            key={queuedItemId}
                            data-chat-queued-composer-item
                            data-chat-queued-composer-editing={isEditingQueuedItem ? "true" : undefined}
                            sx={{
                              minHeight: 32,
                              px: 1.25,
                              py: 0.75,
                              borderRadius: "14px",
                              backgroundColor: isDarkMode
                                ? "rgba(37,37,37,0.96)"
                                : "rgba(236,238,243,0.96)",
                              border: isDarkMode
                                ? "1px solid rgba(255,255,255,0.08)"
                                : "1px solid rgba(0,0,0,0.06)",
                              boxShadow: isDarkMode
                                ? "0 8px 24px rgba(0,0,0,0.28)"
                                : "0 8px 20px rgba(15,23,42,0.10)",
                              display: "flex",
                              flexDirection: isEditingQueuedItem ? "column" : "row",
                              alignItems: isEditingQueuedItem ? "stretch" : "center",
                              gap: isEditingQueuedItem ? 0.75 : 1,
                              pointerEvents: "auto",
                            }}
                          >
                            {isEditingQueuedItem ? (
                              <>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    gap: 1,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      flexShrink: 0,
                                      fontSize: "0.68rem",
                                      fontWeight: 700,
                                      letterSpacing: "0.02em",
                                      textTransform: "uppercase",
                                      color: isDarkMode
                                        ? "rgba(255,255,255,0.62)"
                                        : "rgba(18,18,18,0.54)",
                                    }}
                                  >
                                    {queuedLabel}
                                  </Box>
                                  <Box
                                    sx={{
                                      fontSize: "0.68rem",
                                      fontWeight: 700,
                                      color: isDarkMode
                                        ? "rgba(255,255,255,0.48)"
                                        : "rgba(18,18,18,0.44)",
                                    }}
                                  >
                                    Editing
                                  </Box>
                                </Box>
                                <Box
                                  component="textarea"
                                  ref={queuedEditTextareaRef}
                                  aria-label="Edit queued message"
                                  value={queuedEditDraft}
                                  onChange={(event) => setQueuedEditDraft(event.target.value)}
                                  onKeyDown={(event) => {
                                    if (event.key === "Escape") {
                                      event.preventDefault();
                                      cancelQueuedMessageEdit();
                                      return;
                                    }
                                    if (
                                      event.key === "Enter" &&
                                      !event.shiftKey &&
                                      !isMobileBrowser()
                                    ) {
                                      event.preventDefault();
                                      saveQueuedMessageEdit();
                                    }
                                  }}
                                  sx={{
                                    width: "100%",
                                    boxSizing: "border-box",
                                    minHeight: 34,
                                    maxHeight: 120,
                                    p: "8px 9px",
                                    borderRadius: "10px",
                                    border: isDarkMode
                                      ? "1px solid rgba(255,255,255,0.14)"
                                      : "1px solid rgba(0,0,0,0.10)",
                                    outline: "none",
                                    resize: "vertical",
                                    backgroundColor: isDarkMode
                                      ? "rgba(255,255,255,0.06)"
                                      : "rgba(255,255,255,0.68)",
                                    color: isDarkMode ? "#f5f5f5" : "#121212",
                                    font: "inherit",
                                    fontSize: "0.86rem",
                                    fontWeight: 600,
                                    lineHeight: 1.35,
                                    "&:focus": {
                                      borderColor: isDarkMode
                                        ? "rgba(255,255,255,0.34)"
                                        : "rgba(18,18,18,0.26)",
                                    },
                                  }}
                                />
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "flex-end",
                                    gap: 0.5,
                                  }}
                                >
                                  <Tooltip title="Cancel edit" arrow enterDelay={250} describeChild>
                                    <IconButton
                                      size="small"
                                      aria-label="Cancel queued message edit"
                                      onClick={cancelQueuedMessageEdit}
                                      sx={{
                                        width: 26,
                                        height: 26,
                                        color: isDarkMode
                                          ? "rgba(255,255,255,0.76)"
                                          : "rgba(18,18,18,0.58)",
                                        "&:hover": {
                                          backgroundColor: isDarkMode
                                            ? "rgba(255,255,255,0.10)"
                                            : "rgba(0,0,0,0.07)",
                                        },
                                      }}
                                    >
                                      <CloseRoundedIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Save queued message" arrow enterDelay={250} describeChild>
                                    <span>
                                      <IconButton
                                        size="small"
                                        aria-label="Save queued message"
                                        disabled={!queuedEditDraft.trim()}
                                        onClick={saveQueuedMessageEdit}
                                        sx={{
                                          width: 26,
                                          height: 26,
                                          color: isDarkMode ? "#111827" : "#ffffff",
                                          backgroundColor: isDarkMode ? "#f5f5f5" : "#121212",
                                          opacity: queuedEditDraft.trim() ? 1 : 0.45,
                                          "&:hover": {
                                            backgroundColor: isDarkMode ? "#ffffff" : "#000000",
                                          },
                                          "&.Mui-disabled": {
                                            color: isDarkMode ? "#111827" : "#ffffff",
                                            backgroundColor: isDarkMode ? "#f5f5f5" : "#121212",
                                          },
                                        }}
                                      >
                                        <CheckRoundedIcon sx={{ fontSize: 17 }} />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                </Box>
                              </>
                            ) : (
                              <>
                                <Box
                                  sx={{
                                    flexShrink: 0,
                                    fontSize: "0.68rem",
                                    fontWeight: 700,
                                    letterSpacing: "0.02em",
                                    textTransform: "uppercase",
                                    color: isDarkMode
                                      ? "rgba(255,255,255,0.62)"
                                      : "rgba(18,18,18,0.54)",
                                  }}
                                >
                                  {queuedLabel}
                                </Box>
                                <Box
                                  sx={{
                                    flex: 1,
                                    minWidth: 0,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    fontSize: "0.84rem",
                                    fontWeight: 600,
                                    color: isDarkMode ? "#f5f5f5" : "#121212",
                                  }}
                                >
                                  {queuedItem.message || "Attachment"}
                                </Box>
                                <Tooltip title="Edit queued message" arrow enterDelay={250} describeChild>
                                  <IconButton
                                    size="small"
                                    aria-label="Edit queued message"
                                    onClick={() => startQueuedMessageEdit(queuedItem)}
                                    sx={{
                                      width: 24,
                                      height: 24,
                                      flexShrink: 0,
                                      color: isDarkMode
                                        ? "rgba(255,255,255,0.76)"
                                        : "rgba(18,18,18,0.58)",
                                      "&:hover": {
                                        backgroundColor: isDarkMode
                                          ? "rgba(255,255,255,0.10)"
                                          : "rgba(0,0,0,0.07)",
                                      },
                                    }}
                                  >
                                    <EditIcon sx={{ fontSize: 15 }} />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Remove queued message" arrow enterDelay={250} describeChild>
                                  <IconButton
                                    size="small"
                                    aria-label="Remove queued message"
                                    onClick={() =>
                                      onRemoveQueuedMessage?.(
                                        activeThread?.threadUuid,
                                        queuedItem.clientQueueId,
                                      )
                                    }
                                    sx={{
                                      width: 24,
                                      height: 24,
                                      flexShrink: 0,
                                      color: isDarkMode
                                        ? "rgba(255,255,255,0.76)"
                                        : "rgba(18,18,18,0.58)",
                                      "&:hover": {
                                        backgroundColor: isDarkMode
                                          ? "rgba(255,255,255,0.10)"
                                          : "rgba(0,0,0,0.07)",
                                      },
                                    }}
                                  >
                                    <CloseRoundedIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  )}

                  {slashOpen && (
                    <SlashCommandMenu
                      commands={slashMatches}
                      activeIndex={activeSlashIndex}
                      bottomOffset={
                        bubbleH +
                        controlsRowHeight +
                        tickerPreviewTrayHeight +
                        8
                      }
                      onSelect={applySlashCommand}
                      onHighlight={setSlashIndex}
                    />
                  )}

                  {/* Wrapper for bubble internals */}
                  <Box
                    sx={{
                      position: "absolute",
                      left: 0,
                      right: 0,
                      bottom: 0,
                      height: `${bubbleH + controlsRowHeight + tickerPreviewTrayHeight}px`,
                    }}
                  >
                    <Box
                      sx={{
                        position: "absolute",
                        inset: 0,
                        borderRadius: "26px",
                        backgroundColor: isDarkMode ? "#2f2f2f" : "#ffffff",
                        border: isDarkMode
                          ? "1px solid rgba(255,255,255,0.06)"
                          : "1px solid rgba(0,0,0,0.08)",
                        boxShadow: isDarkMode
                          ? "0 8px 24px rgba(0,0,0,0.24)"
                          : "0 8px 20px rgba(15,23,42,0.08)",
                        pointerEvents: "none",
                      }}
                    />

                    {expandToggleVisible && (
                      <Tooltip
                        title={manuallyExpanded ? "Collapse input" : "Expand input"}
                        arrow
                        enterDelay={250}
                        describeChild
                      >
                        <IconButton
                          onClick={() => setManuallyExpanded((v) => !v)}
                          sx={{
                            position: "absolute",
                            top: `${tickerPreviewTrayHeight + 6}px`,
                            right: "8px",
                            width: 26,
                            height: 26,
                            zIndex: 5,
                            borderRadius: "50%",
                            color: isDarkMode
                              ? "rgba(255,255,255,0.6)"
                              : "rgba(18,18,18,0.55)",
                            backgroundColor: "transparent",
                            opacity: isTypingNow ? 0 : 1,
                            pointerEvents: isTypingNow ? "none" : "auto",
                            transition: "opacity 220ms ease, transform 0.2s ease",
                            "&:hover": {
                              backgroundColor: isDarkMode
                                ? "rgba(255,255,255,0.08)"
                                : "rgba(0,0,0,0.06)",
                            },
                            "&:active": {
                              transform: "scale(0.92)",
                            },
                          }}
                          aria-label={
                            manuallyExpanded ? "Collapse input" : "Expand input"
                          }
                        >
                          {manuallyExpanded ? (
                            <CloseFullscreenRoundedIcon sx={{ fontSize: 15 }} />
                          ) : (
                            <OpenInFullRoundedIcon sx={{ fontSize: 15 }} />
                          )}
                        </IconButton>
                      </Tooltip>
                    )}

                    <Tooltip title="Add photos, files, and more" arrow enterDelay={250} describeChild>
                      <IconButton
                        onClick={(event) => {
                          if (inputLocked) return;
                          if (props.isDesktop) {
                            setPlusMenuAnchorEl(event.currentTarget);
                          } else {
                            setAddSheetOpen(true);
                          }
                        }}
                        disabled={inputLocked}
                        sx={{
                          position: "absolute",
                          left: "10px",
                          bottom: "7px",
                          width: 30,
                          height: 30,
                          zIndex: 4,
                          borderRadius: "50%",
                          backgroundColor: "transparent",
                          color: isDarkMode ? "#fff" : "#000",
                          transition: "transform 0.2s ease, opacity 0.2s ease",
                          "&:hover": {
                            backgroundColor: "transparent",
                            transform: "scale(1.08)",
                          },
                          "&:active": {
                            transform: "scale(0.95)",
                            backgroundColor: "transparent",
                          },
                        }}
                        aria-label="Attach Images"
                      >
                        <AddIcon sx={{ fontSize: 22 }} />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title={rightInputTooltip} arrow enterDelay={250} describeChild>
                      <IconButton
                        onClick={handleRightInputAction}
                        disabled={rightInputDisabled}
                        sx={{
                          position: "absolute",
                          right: "8px",
                          bottom: "6px",
                          width: 32,
                          height: 32,
                          zIndex: 4,
                          borderRadius: "50%",
                          backgroundColor: actionCircleBg,
                          color: actionIconColor,
                          transition: "transform 0.2s ease, opacity 0.2s ease",
                          "&:hover": {
                            backgroundColor: actionCircleBg,
                            transform: "scale(1.06)",
                          },
                          "&:active": {
                            transform: "scale(0.95)",
                            backgroundColor: actionCircleBg,
                          },
                          "&.Mui-disabled": {
                            backgroundColor: actionCircleBg,
                            opacity: 0.45,
                          },
                        }}
                        aria-label={rightInputAriaLabel}
                      >
                        <Box
                          className={rightInputAction === "send" ? arrowClass : undefined}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            "@keyframes micInputPulse": {
                              "0%": { transform: "scale(1)", opacity: 0.72 },
                              "50%": { transform: "scale(1.14)", opacity: 1 },
                              "100%": { transform: "scale(1)", opacity: 0.72 },
                            },
                            animation: rightInputAction === "mic" && isRecording
                              ? "micInputPulse 1.15s ease-in-out infinite"
                              : "none",
                          }}
                        >
                          {rightInputAction === "stop" ? (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="15"
                              height="15"
                              viewBox="0 0 12 12"
                              fill="none"
                            >
                              <rect
                                x="2"
                                y="2"
                                width="8"
                                height="8"
                                rx="1.5"
                                fill="#DC2626"
                              />
                            </svg>
                          ) : rightInputAction === "send" ? (
                            <svg xmlns="http://www.w3.org/2000/svg" width="11" height="15" viewBox="0 0 9 12" fill="none">
                              <path
                                d="M8.08167 4.54083L4.54083 1L1 4.54083M4.54083 10.9167V1.09917"
                                stroke={actionIconColor}
                                strokeWidth="1.5"
                                strokeMiterlimit="10"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          ) : (
                            <MicIcon sx={{ fontSize: 20 }} />
                          )}
                        </Box>
                      </IconButton>
                    </Tooltip>

                    {/* Attachments strip pinned to the TOP of the bubble */}
                    {/* Allow for free */}
                    {/* {hasSubscription && pendingAttachments.length > 0 && ( */}
                    {pendingAttachments.length > 0 && (
                      <Box
                        ref={attachmentsRef}
                        sx={{
                          position: "absolute",
                          left: 0,
                          right: 0,
                          top: `${tickerPreviewTrayHeight}px`,
                          padding: "8px 40px 4px 22px",
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "8px",
                          maxHeight: "96px",
                          overflowY: "auto",
                          zIndex: 2,
                        }}
                      >
                        {pendingAttachments.map((it, idx) => {
                          const src = previewUrls[idx];
                          return (
                          <Box
                            key={idx}
                            sx={{ position: "relative", flex: "0 0 auto", pr: "10px", pb: "10px", mt: "6px" }}
                          >
                            <Box
                              sx={{
                                width: 54, height: 54,
                                borderRadius: "10px", overflow: "hidden", boxShadow: "0 0 0 1px rgba(0,0,0,0.06)",
                                backgroundColor: isDarkMode ? "#1e1e1e" : "#f5f5f5",
                              }}
                            >
                              {(() => {
                                const f = it?.file || it?.blob || it;
                                const mime = normalizeMime(it?.mime || f?.type || "");
                                const isImage = mime.startsWith("image/");
                                const isPdf = mime === "application/pdf";
                                const isText = mime === "text/plain";
                                const isCsv = mime === "text/csv" || mime === "application/csv";
                                const isDocument = mime === "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
                                const isSpreadsheet = mime === "application/vnd.ms-excel" || mime === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
                                const isAudio = mime.startsWith("audio/");
                                const label = isAudio ? "AUDIO" : isPdf ? "PDF" : isText ? "TXT" : isCsv ? "CSV" : isDocument ? "DOCX" : isSpreadsheet ? "XLS" : "FILE";
                                if (isImage && src) {
                                  return (
                                    <img
                                      src={src}
                                      alt={`attachment-${idx}`}
                                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                      onLoad={measureAttachments}
                                    />
                                  );
                                }
                                return (
                                  <Box
                                    title={f?.name || it?.filename || label}
                                    sx={{
                                      width: "100%",
                                      height: "100%",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      fontSize: "0.65rem",
                                      fontWeight: 700,
                                      letterSpacing: "0.04em",
                                      color: isDarkMode ? "#f1f1f1" : "#111",
                                      backgroundColor: isDarkMode ? "#2a2a2a" : "#e9e9ef",
                                    }}
                                  >
                                    {label}
                                  </Box>
                                );
                              })()}
                            </Box>
                            <IconButton
                              size="small"
                              onClick={() => removeAttachmentAt(idx)}
                              disabled={inputLocked}
                              sx={{
                                position: "absolute",
                                top: -8,
                                right: -2,
                                width: 22,
                                height: 22,
                                zIndex: 1,
                                bgcolor: isDarkMode ? "rgba(0,0,0,0.6)" : "rgba(255,255,255,0.6)",
                                boxShadow: "0 0 0 1px rgba(0,0,0,0.08)",
                                "&:hover": {
                                  bgcolor: isDarkMode ? "rgba(0,0,0,0.75)" : "white",
                                },
                              }}
                              aria-label="Remove attachment"
                            >
                              <CloseRoundedIcon
                                sx={{
                                  fontSize: 16,
                                  color: isDarkMode ? "#F7F7F7" : "#121212",
                                }}
                              />
                            </IconButton>
                          </Box>
                        )})}
                      </Box>
                    )}

                    {editingMessage && (
                      <Box
                        sx={{
                          position: "absolute",
                          left: 10,
                          right: 10,
                          top:
                            tickerPreviewTrayHeight +
                            (attachmentsH ? attachmentsH + 6 : 6),
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 1,
                          px: 1.25,
                          py: 0.3,
                          height: '24px',
                          borderRadius: "10px",
                          backgroundColor: isDarkMode ? "rgba(0,0,0,0.30)" : "rgba(255,255,255,0.55)",
                          fontSize: "0.75rem",
                          zIndex: 3,
                          pointerEvents: "auto",
                        }}
                      >
                        <EditIcon fontSize="small" sx={{ color: isDarkMode ? "#ddd" : "#555" }} />
                        <Box className="base-text" sx={{color: isDarkMode ? "#ddd" : "#555", fontSize: '12px'}}>Editing previous message</Box>
                        <IconButton
                          size="small"
                          onClick={handleCancelEdit}
                          disabled={inputLocked}
                          sx={{
                            position: 'absolute', right: '10px',
                            ml: 0.25,
                            width: 18,
                            height: 18,
                            padding: 0,
                          }}
                          aria-label="Cancel editing"
                        >
                          <CloseRoundedIcon
                            sx={{
                              fontSize: 15,
                              color: isDarkMode ? "#e5e5e5" : "#374151",
                            }}
                          />
                        </IconButton>
                      </Box>
                    )}

                    {/* Textarea: typing starts below attachments by paddingTop */}
                    <textarea
                      className={isDarkMode ? "placeholder-input dark" : "placeholder-input"}
                      value={localQuestion}
                      disabled={inputLocked}
                      onChange={(e) => {
                        setLocalQuestion(e.target.value);
                        markTyping();
                        requestAnimationFrame(() => {
                          measureAttachments();
                        });
                      }}
                      onCompositionEnd={(e) => {
                        setLocalQuestion(e.target.value);
                        requestAnimationFrame(() => {
                          measureAttachments();
                        });
                      }}
                      onKeyDown={(k) => {
                        if (slashOpen) {
                          if (k.key === "ArrowUp") {
                            k.preventDefault();
                            setSlashIndex(
                              (activeSlashIndex - 1 + slashMatches.length) %
                                slashMatches.length,
                            );
                            return;
                          }
                          if (k.key === "ArrowDown") {
                            k.preventDefault();
                            setSlashIndex(
                              (activeSlashIndex + 1) % slashMatches.length,
                            );
                            return;
                          }
                          if (k.key === "Escape") {
                            k.preventDefault();
                            setSlashDismissed(true);
                            return;
                          }
                          if ((k.key === "Enter" && !k.shiftKey) || k.key === "Tab") {
                            k.preventDefault();
                            applySlashCommand(slashMatches[activeSlashIndex]);
                            return;
                          }
                        }
                        if (k.key === "Escape" && editingMessage) {
                          handleCancelEdit();
                          return;
                        }
                        if (k.code === "Enter" && !k.shiftKey && !isMobileBrowser()) {
                          k.preventDefault();
                          submitQuestion();
                        }
                      }}
                      ref={questionInputRef}
                      onFocus={onFocus}
                      onBlur={onBlur}
                      placeholder={inputPlaceholderText}
                      onPaste={onTextareaPaste}
                      style={{
                        bottom: `${controlsRowHeight}px`,
                        paddingTop: `${12 + attachmentsH + editTextareaTopOffset}px`,
                        paddingLeft: "18px",
                        paddingRight: "36px",
                        fontSize: "16px",
                        fontWeight: 400,
                        lineHeight: "22px",
                        minHeight:
                          manuallyExpanded && !editingMessage
                            ? "38vh"
                            : "28px",
                        maxHeight: editingMessage
                          ? "108px"
                          : manuallyExpanded
                          ? "46vh"
                          : "200px",
                        // Animates expand/collapse only: the toggle drives
                        // min/max-height, while typing growth changes
                        // `height` (autosize) and stays instant. The card
                        // follows via the ResizeObserver -> bubbleH path.
                        transition: composerHeightTransition,
                        backgroundColor: "transparent",
                        opacity: inputLocked ? 0.72 : 1,
                        cursor: inputLocked ? "not-allowed" : "text",
                      }}
                    />
                  </Box>

                  {/* Allow for free */}
                  {/* {hasSubscription && ( */}
                  {true && (
                    <Box sx={{ display: "none" }}>
                      <ImagePicker key={pickerNonce} ref={pickerRef} onFiles={handleFiles} />
                    </Box>
                  )}

                  <ComposerPlusMenu
                    anchorEl={plusMenuAnchorEl}
                    open={Boolean(plusMenuAnchorEl)}
                    onClose={() => setPlusMenuAnchorEl(null)}
                    showReasoning={hasActiveSubscription}
                    reasoningMode={userReasoningMode}
                    onSelectReasoning={changeReasoningMode}
                    showResponseLength={showResponseLength}
                    responseLength={userResponseLength}
                    onSelectResponseLength={changeResponseLength}
                    onPickPhotos={openPhotoPicker}
                    onPickFiles={openFilePicker}
                  />
                  <ComposerAddSheet
                    open={addSheetOpen}
                    onClose={() => setAddSheetOpen(false)}
                    showReasoning={hasActiveSubscription}
                    reasoningMode={userReasoningMode}
                    onSelectReasoning={changeReasoningMode}
                    showResponseLength={showResponseLength}
                    responseLength={userResponseLength}
                    onSelectResponseLength={changeResponseLength}
                    onPickPhotos={openPhotoPicker}
                    onPickFiles={openFilePicker}
                  />

                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
export default Input;
